import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { AppLayout } from "@/components/layout/AppLayout";
import { AnalyticsTracker } from "@/components/shared/AnalyticsTracker";
import { ScrollToTop } from "@/components/shared/ScrollToTop";
import { NiceLoader } from "@/components/shared/NiceLoader";
import { BackToTop } from "@/components/shared/BackToTop";

import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import ServicesRegistry from "./pages/ServicesRegistry";
import Settings from "./pages/Settings";
import Integrations from "./pages/Integrations";
import Incidents from "./pages/Incidents";

// Lazy-loaded pages for code splitting & performance optimization
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const StatusPage = lazy(() => import("./pages/StatusPage"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Reports = lazy(() => import("./pages/Reports"));
const IncidentDetail = lazy(() => import("./pages/IncidentDetail"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Docs = lazy(() => import("./pages/Docs"));
const PublicRegistry = lazy(() => import("./pages/PublicRegistry"));
const Tutorials = lazy(() => import("./pages/Tutorials"));
const Pricing = lazy(() => import("./pages/Pricing"));
const RadarPage = lazy(() => import("./pages/Radar"));
const SlugStatusPage = lazy(() => import("./pages/SlugStatusPage"));
const WpConnect = lazy(() => import("./pages/WpConnect"));
const StatusDesigner = lazy(() => import("./pages/StatusDesigner"));
const SharedReport = lazy(() => import("./pages/SharedReport"));
const Heartbeats = lazy(() => import("./pages/Heartbeats"));
const HeartbeatDetail = lazy(() => import("./pages/HeartbeatDetail"));
const SlackLanding = lazy(() => import("./pages/SlackLanding"));
const DnsCallback = lazy(() => import("./pages/DnsCallback"));
const CloudflareOAuth = lazy(() => import("./pages/CloudflareOAuth"));
const CloudflareCallback = lazy(() => import("./pages/CloudflareCallback"));
const GithubCallback = lazy(() => import("./pages/GithubCallback"));
const OAuthAuthorize = lazy(() => import("./pages/OAuthAuthorize"));
const Features = lazy(() => import("./pages/Features"));
const CustomDomainStatusPage = lazy(() => import("./pages/CustomDomainStatusPage"));
const UptimeRobotAlternative = lazy(() => import("./pages/UptimeRobotAlternative"));
const BetterStackAlternative = lazy(() => import("./pages/BetterStackAlternative"));
const PingdomAlternative = lazy(() => import("./pages/PingdomAlternative"));
const UptimeKumaAlternative = lazy(() => import("./pages/UptimeKumaAlternative"));
const CronitorAlternative = lazy(() => import("./pages/CronitorAlternative"));
const PublicStatusPage = lazy(() => import("./pages/PublicStatusPage"));
const StatusPageBuilder = lazy(() => import("./pages/StatusPageBuilder"));
const Domains = lazy(() => import("./pages/Domains"));
const AiAppsLanding = lazy(() => import("./pages/AiAppsLanding"));
const PlatformHealth = lazy(() => import("./pages/PlatformHealth"));
const OnboardingWizard = lazy(() => import("./pages/OnboardingWizard"));
const FounderBadge = lazy(() => import("./pages/FounderBadge"));
const VerifyFounder = lazy(() => import("./pages/VerifyFounder"));

// SEO / GEO / Blog Pages
const ComparisonPage = lazy(() => import("./pages/vs/ComparisonPage"));
const UseCasePage = lazy(() => import("./pages/for/UseCasePage"));
const PlatformStatus = lazy(() => import("./pages/PlatformStatus"));
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Security = lazy(() => import("./pages/Security"));

// New Dynamic pSEO / AEO templates
const AlternativeTemplate = lazy(() => import("./pages/alternatives/AlternativeTemplate"));
const FrameworkTemplate = lazy(() => import("./pages/cron/FrameworkTemplate"));
const CronGeneratorTool = lazy(() => import("./pages/tools/CronGeneratorTool"));
const SslExpiryChecker = lazy(() => import("./pages/tools/SslExpiryChecker"));
const DnsChecker = lazy(() => import("./pages/tools/DnsChecker"));
const FreeToolsHub = lazy(() => import("./pages/tools/FreeToolsHub"));
const VercelErrorDecoder = lazy(() => import("./pages/tools/VercelErrorDecoder"));
const IsVercelDown = lazy(() => import("./pages/tools/IsVercelDown"));
const DbConnectionParser = lazy(() => import("./pages/tools/DbConnectionParser"));
const EnvValidator = lazy(() => import("./pages/tools/EnvValidator"));
const StackTraceExplainer = lazy(() => import("./pages/tools/StackTraceExplainer"));
const StripeWebhookDebugger = lazy(() => import("./pages/tools/StripeWebhookDebugger"));
const GhaLogAnalyzer = lazy(() => import("./pages/tools/GhaLogAnalyzer"));
const SentryToGithub = lazy(() => import("./pages/tools/SentryToGithub"));
const IncidentCostCalculator = lazy(() => import("./pages/tools/IncidentCostCalculator"));
const MttrCalculator = lazy(() => import("./pages/tools/MttrCalculator"));
const Grok4ErrorDecoder = lazy(() => import("./pages/tools/Grok4ErrorNotFound"));
const BunInstallFix = lazy(() => import("./pages/tools/BunInstallFix"));
const VercelEdgeConfigError = lazy(() => import("./pages/tools/VercelEdgeConfigError"));
const ClaudeModelError = lazy(() => import("./pages/tools/ClaudeModelError"));
const SupabaseVectorFix = lazy(() => import("./pages/tools/SupabaseVectorFix"));
const AiStatusDashboard = lazy(() => import("./pages/tools/AiStatusDashboard"));
const FlyOomDebugger = lazy(() => import("./pages/tools/FlyOomDebugger"));
const CloudflareR2Conflict = lazy(() => import("./pages/tools/CloudflareR2Conflict"));
const NextjsCorsDebugger = lazy(() => import("./pages/tools/NextjsCorsDebugger"));
const BunMemoryLeakAnalyzer = lazy(() => import("./pages/tools/BunMemoryLeakAnalyzer"));
const VercelTimeoutAnalyzer = lazy(() => import("./pages/tools/VercelTimeoutAnalyzer"));
const GeminiPayloadValidator = lazy(() => import("./pages/tools/GeminiPayloadValidator"));
const TanstackStartDebugger = lazy(() => import("./pages/tools/TanstackStartDebugger"));
const CloudflareWorkersNodeCompat = lazy(() => import("./pages/tools/CloudflareWorkersNodeCompat"));
const ClaudeContextAnalyzer = lazy(() => import("./pages/tools/ClaudeContextAnalyzer"));
const BunNodeCompat = lazy(() => import("./pages/tools/BunNodeCompat"));
const SupabaseVectorSchema = lazy(() => import("./pages/tools/SupabaseVectorSchema"));
const GrokRequestValidator = lazy(() => import("./pages/tools/GrokRequestValidator"));

// SEO Landing Pages (money pages, AI distribution, AEO, regional)
const SeoLandingPage = lazy(() => import("./pages/SeoLandingPage"));
const RegionalLandingPage = lazy(() =>
  import("./pages/SeoLandingPage").then((m) => ({ default: m.RegionalLandingPage }))
);


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

queryClient.setQueryDefaults(["websites"], {
  queryFn: async () => {
    // If somehow a raw websites query is fired without user context, gracefully return empty or try fetching
    try {
      const { api } = await import('@/lib/api-client');
      return await api.getSites();
    } catch {
      return [];
    }
  }
});

const isDefaultDomain = (hostname: string) => {
  const lowercase = hostname.toLowerCase();
  
  // Local host dev
  if (lowercase === "localhost" || lowercase === "127.0.0.1") return true;
  
  // Platform deployments/subdomains
  if (
    lowercase.endsWith(".workers.dev") ||
    lowercase.endsWith(".pages.dev") ||
    lowercase.endsWith(".vercel.app") ||
    lowercase.endsWith(".netlify.app")
  ) {
    return true;
  }
  
  // Root domain and subdomains of uptimewing.com
  if (lowercase === "uptimewing.com" || lowercase.endsWith(".uptimewing.com")) {
    return true;
  }
  
  // Legacy domain structures
  if (lowercase.endsWith(".uptimecrow.com") || lowercase.endsWith(".watchora.com")) {
    return true;
  }

  return false;
};

const App = () => {
  const hostname = window.location.hostname;
  
  if (!isDefaultDomain(hostname)) {
    return (
      <QueryClientProvider client={queryClient}>
        <PublicStatusPage />
      </QueryClientProvider>
    );
  }

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <BackToTop />
            <AnalyticsTracker />
            <Suspense fallback={<NiceLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/features" element={<Features />} />
                <Route path="/login" element={<Login />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/onboarding" element={<OnboardingWizard />} />
                <Route path="/uptimerobot-alternative" element={<UptimeRobotAlternative />} />
                <Route path="/betterstack-alternative" element={<BetterStackAlternative />} />
                <Route path="/pingdom-alternative" element={<PingdomAlternative />} />
                <Route path="/alternatives/alternative-to-uptimerobot" element={<UptimeRobotAlternative />} />
                <Route path="/alternatives/alternative-to-uptime-kuma" element={<UptimeKumaAlternative />} />
                <Route path="/alternatives/alternative-to-better-stack" element={<BetterStackAlternative />} />
                <Route path="/alternatives/alternative-to-cronitor" element={<CronitorAlternative />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/status" element={<StatusPage />} />
                <Route path="/status/:slug" element={<PublicStatusPage />} />
                <Route path="/p/:slug" element={<PublicStatusPage />} />
                <Route path="/s/:slug" element={<SlugStatusPage />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/docs/:slug" element={<Docs />} />
                <Route path="/archive/:id" element={<PublicRegistry />} />
                <Route path="/status-legacy/:id" element={<PublicRegistry />} />
                <Route path="/integrations/wordpress/connect" element={<WpConnect />} />
                <Route path="/r/:token" element={<SharedReport />} />
                <Route path="/founders/:id" element={<FounderBadge />} />
                <Route path="/verify-founder" element={<VerifyFounder />} />
                <Route path="/integrations/slack" element={<SlackLanding />} />
                <Route path="/oauth/:provider/authorize" element={<CloudflareOAuth />} />
                <Route path="/oauth/cloudflare/callback" element={<CloudflareCallback />} />
                <Route path="/oauth/github/callback" element={<GithubCallback />} />
                <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
                
                {/* AI Apps public discovery routes */}
                <Route path="/ai" element={<AiAppsLanding />} />
                <Route path="/chatgpt" element={<AiAppsLanding />} />
                <Route path="/use-with-chatgpt" element={<AiAppsLanding />} />
                <Route path="/apps" element={<AiAppsLanding />} />

                {/* Comparison & pSEO Routes */}
                <Route path="/vs/:competitor" element={<ComparisonPage />} />
                <Route path="/alternatives/:competitor" element={<AlternativeTemplate />} />
                <Route path="/cron/:framework" element={<FrameworkTemplate />} />
                <Route path="/tools" element={<FreeToolsHub />} />
                <Route path="/tools/cron-expression-generator" element={<CronGeneratorTool />} />
                <Route path="/tools/ssl-expiry-checker" element={<SslExpiryChecker />} />
                <Route path="/tools/dns-checker" element={<DnsChecker />} />
                <Route path="/tools/vercel-error-decoder" element={<VercelErrorDecoder />} />
                <Route path="/tools/vercel-timeout-analyzer" element={<VercelTimeoutAnalyzer />} />
                <Route path="/tools/is-vercel-down" element={<IsVercelDown />} />
                <Route path="/tools/parse-db-url" element={<DbConnectionParser />} />
                <Route path="/tools/validate-env" element={<EnvValidator />} />
                <Route path="/tools/explain-stack-trace" element={<StackTraceExplainer />} />
                <Route path="/tools/debug-stripe-webhook" element={<StripeWebhookDebugger />} />
                <Route path="/tools/analyze-gha-logs" element={<GhaLogAnalyzer />} />
                <Route path="/tools/sentry-to-github" element={<SentryToGithub />} />
                <Route path="/tools/incident-cost" element={<IncidentCostCalculator />} />
                <Route path="/tools/mttr-calculator" element={<MttrCalculator />} />
                <Route path="/tools/grok-4-error-not-found" element={<Grok4ErrorDecoder />} />
                <Route path="/tools/bun-install-fails" element={<BunInstallFix />} />
                <Route path="/tools/vercel-edge-config-not-found" element={<VercelEdgeConfigError />} />
                <Route path="/tools/claude-model-error-not-found" element={<ClaudeModelError />} />
                <Route path="/tools/supabase-vector-dimension-mismatch" element={<SupabaseVectorFix />} />
                <Route path="/tools/is-claude-down" element={<AiStatusDashboard />} />
                <Route path="/tools/fly-io-out-of-memory" element={<FlyOomDebugger />} />
                <Route path="/tools/cloudflare-r2-409-error" element={<CloudflareR2Conflict />} />
                <Route path="/tools/nextjs-cors-debugger" element={<NextjsCorsDebugger />} />
                <Route path="/tools/bun-memory-leak-analyzer" element={<BunMemoryLeakAnalyzer />} />
                <Route path="/tools/gemini-payload-validator" element={<GeminiPayloadValidator />} />
                <Route path="/tools/tanstack-start-routing-debugger" element={<TanstackStartDebugger />} />
                <Route path="/tools/cloudflare-workers-node-compatibility" element={<CloudflareWorkersNodeCompat />} />
                <Route path="/tools/claude-conversation-context-analyzer" element={<ClaudeContextAnalyzer />} />
                <Route path="/tools/bun-node-compatibility" element={<BunNodeCompat />} />
                <Route path="/tools/supabase-vector-schema-generator" element={<SupabaseVectorSchema />} />
                <Route path="/tools/grok-api-request-validator" element={<GrokRequestValidator />} />
                <Route path="/for/:usecase" element={<UseCasePage />} />
                <Route path="/status/:platform" element={<PlatformStatus />} />

                {/* ── LAYER 1: Money Pages ─────────────────────────────────── */}
                 <Route path="/website-monitoring" element={<SeoLandingPage />} />
                 <Route path="/api-monitoring" element={<SeoLandingPage />} />
                 <Route path="/cron-job-monitoring" element={<SeoLandingPage />} />
                 <Route path="/heartbeat-monitoring" element={<SeoLandingPage />} />
                 <Route path="/status-pages" element={<SeoLandingPage />} />
                 <Route path="/uptime-monitoring" element={<SeoLandingPage />} />
                 <Route path="/ssl-monitoring" element={<SeoLandingPage />} />
                 <Route path="/domain-monitoring" element={<SeoLandingPage />} />
                 <Route path="/incident-management" element={<SeoLandingPage />} />
                 <Route path="/auto-healing-monitoring" element={<SeoLandingPage />} />
                 <Route path="/chatgpt-monitoring" element={<SeoLandingPage />} />
                 <Route path="/free-website-monitoring" element={<SeoLandingPage />} />
                 <Route path="/pagerduty-alternative" element={<SeoLandingPage />} />

                {/* ── LAYER 3: Regional SEO Pages ──────────────────────────── */}
                <Route path="/uptime-monitoring-india" element={<SeoLandingPage />} />
                <Route path="/uptime-monitoring-uk" element={<SeoLandingPage />} />
                <Route path="/uptime-monitoring-usa" element={<SeoLandingPage />} />
                <Route path="/uptime-monitoring-australia" element={<SeoLandingPage />} />
                <Route path="/uptime-monitoring-canada" element={<SeoLandingPage />} />
                <Route path="/uptime-monitoring-germany" element={<SeoLandingPage />} />

                {/* ── LAYER 4: AI Distribution Pages ──────────────────────── */}
                <Route path="/monitor-from-chatgpt" element={<SeoLandingPage />} />
                <Route path="/mcp-monitoring" element={<SeoLandingPage />} />

                {/* ── LAYER 8: AEO / Learn Pages ───────────────────────────── */}
                <Route path="/learn/what-is-uptime-monitoring" element={<SeoLandingPage />} />
                <Route path="/learn/how-auto-healing-works" element={<SeoLandingPage />} />
                <Route path="/learn/what-is-a-status-page" element={<SeoLandingPage />} />
                
                {/* Blog System Routes */}
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                
                {/* Core Info Routes */}
                <Route path="/about" element={<About />} />
                <Route path="/changelog" element={<Changelog />} />
                <Route path="/security" element={<Security />} />

                <Route element={<AppLayout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/monitors" element={<Navigate to="/services" replace />} />
                  <Route path="/monitors/:id" element={<Navigate to="/services/:id" replace />} />
                  <Route path="/monitors/:id/history" element={<Navigate to="/services/:id" replace />} />
                  <Route path="/websites" element={<Navigate to="/services" replace />} />
                  <Route path="/websites/:id" element={<Navigate to="/services/:id" replace />} />
                  <Route path="/websites/:id/history" element={<Navigate to="/services/:id" replace />} />
                  <Route path="/heartbeats" element={<Heartbeats />} />
                  <Route path="/heartbeats/:id" element={<HeartbeatDetail />} />
                  <Route path="/alerts" element={<Alerts />} />
                  <Route path="/incidents" element={<Incidents />} />
                  <Route path="/incidents/:id" element={<IncidentDetail />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/radar" element={<RadarPage />} />
                  <Route path="/status-pages-builder" element={<StatusPageBuilder />} />
                  <Route path="/status-designer" element={<StatusDesigner />} />
                  <Route path="/domains" element={<Domains />} />
                  <Route path="/integrations" element={<Integrations />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/platform-health" element={<PlatformHealth />} />
                  <Route path="/services" element={<ServicesRegistry />} />
                  <Route path="/services/:id" element={<ServiceDetail />} />
                  <Route path="/settings/dns-callback" element={<DnsCallback />} />
                  <Route path="/tutorials" element={<Tutorials />} />
                  {/* Legacy routes redirect */}
                  <Route path="/dashboard/:id" element={<Navigate to="/services/:id" replace />} />
                  <Route path="/dashboard/:id/history" element={<Navigate to="/services/:id" replace />} />
                </Route>
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
  );
};

export default App;
